import { createContext, ReactNode, useContext, useState } from "react";
import { CreateEmployeeError } from "../hooks/use-create-members";

interface SetupSummaryData {
  companiesCreated: number;
  membersCreated: number;
  departmentsCreated: number;
  jobRolesCreated: number;
  companiesConnected: number;
  employeeErrors: CreateEmployeeError[];
}

interface SetupSummaryContextType {
  summaryData: SetupSummaryData;
  incrementCompaniesCreated: (count: number) => void;
  incrementMembersCreated: (count: number) => void;
  incrementDepartmentsCreated: (count?: number) => void;
  incrementJobRolesCreated: (count?: number) => void;
  incrementCompaniesConnected: (count?: number) => void;
  addEmployeeErrors: (errors: CreateEmployeeError[]) => void;
  resetSummary: () => void;
}

const SetupSummaryContext = createContext<SetupSummaryContextType | undefined>(
  undefined
);

const initialSummaryData: SetupSummaryData = {
  employeeErrors: [],
  companiesCreated: 0,
  membersCreated: 0,
  departmentsCreated: 0,
  jobRolesCreated: 0,
  companiesConnected: 0,
};

export const SetupSummaryProvider = ({ children }: { children: ReactNode }) => {
  const [summaryData, setSummaryData] =
    useState<SetupSummaryData>(initialSummaryData);

  const incrementCompaniesCreated = (count: number) => {
    setSummaryData((prev) => ({
      ...prev,
      companiesCreated: prev.companiesCreated + count,
    }));
  };

  const incrementMembersCreated = (count: number) => {
    setSummaryData((prev) => ({
      ...prev,
      membersCreated: prev.membersCreated + count,
    }));
  };

  const incrementDepartmentsCreated = (count = 1) => {
    setSummaryData((prev) => ({
      ...prev,
      departmentsCreated: prev.departmentsCreated + count,
    }));
  };

  const incrementJobRolesCreated = (count = 1) => {
    setSummaryData((prev) => ({
      ...prev,
      jobRolesCreated: prev.jobRolesCreated + count,
    }));
  };

  const incrementCompaniesConnected = (count = 1) => {
    setSummaryData((prev) => ({
      ...prev,
      companiesConnected: prev.companiesConnected + count,
    }));
  };

  const addEmployeeErrors = (errors: CreateEmployeeError[]) => {
    setSummaryData((prev) => ({
      ...prev,
      employeeErrors: [...prev.employeeErrors, ...errors],
    }));
  };

  const resetSummary = () => {
    setSummaryData(initialSummaryData);
  };

  return (
    <SetupSummaryContext.Provider
      value={{
        summaryData,
        incrementCompaniesCreated,
        incrementMembersCreated,
        incrementDepartmentsCreated,
        incrementJobRolesCreated,
        incrementCompaniesConnected,
        addEmployeeErrors,
        resetSummary,
      }}
    >
      {children}
    </SetupSummaryContext.Provider>
  );
};

export const useSetupSummary = () => {
  const context = useContext(SetupSummaryContext);

  if (context === undefined) {
    const noop = () => {
      // Intentionally empty - no-op function for when used outside provider
    };

    return {
      summaryData: {
        employeeErrors: [],
        companiesCreated: 0,
        membersCreated: 0,
        departmentsCreated: 0,
        jobRolesCreated: 0,
        companiesConnected: 0,
      },
      addEmployeeErrors: noop,
      incrementCompaniesCreated: noop,
      incrementMembersCreated: noop,
      incrementDepartmentsCreated: noop,
      incrementJobRolesCreated: noop,
      incrementCompaniesConnected: noop,
      resetSummary: noop,
    };
  }

  return context;
};
